import {Component, Input, OnInit} from '@angular/core';
import {
    ReferenceBookResponse,
    ReferenceBookType,
    TrendApiService,
    TrendIncidentsResponse,
    TrendResponse,
} from '@npck/af-frontend-api';
import {NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {mapIncidentStatusToStyle} from '../../../../../components/status/status.component';
import {TrendDetailsFilterHolderService} from '../../../../../domain/trend-details-filter-holder.service';
import {FilterField} from '../../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../../components/full-table/full-table.component';
import {
    ButtonColumn,
    Column,
    ColumnType,
    ELLIPSIS_LARGE_TEXT_MAX_LENGTH, ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH,
    Row, StatusColumn,
} from '../../../../../components/full-table/table/table.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {INCIDENT_ID} from '../../../../../domain/query-params.keys';
import {Path} from '../../../../../path';

const TREND_INCIDENTS_TAB = 'tabTrendIncidents';
const INCIDENT_DETAILS_TAB = 'tabIncidentDetails';
const INCIDENT_RESOLUTION_TAB = 'tabResolutions';

@Component({
    selector: 'af-trend-incidents-tab',
    standalone: false,
    templateUrl: './trend-incidents-tab.component.html',
    styleUrl: './trend-incidents-tab.component.scss',
})
export class TrendIncidentsTabComponent implements OnInit {
    @Input() trend: TrendResponse;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    totalElements: number;
    response: TrendIncidentsResponse;

    constructor(
        private trendApiService: TrendApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private filterHolderService: TrendDetailsFilterHolderService,
    ) {
    }

    ngOnInit(): void {
        this.loadIncidents(
            this.filterHolderService.getState(this.trend.id, TREND_INCIDENTS_TAB)?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadIncidents(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.trend.id, TREND_INCIDENTS_TAB)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.trend.id, TREND_INCIDENTS_TAB, this.tableEvent);
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.INCIDENT_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.trendApiService.getTrendIncidentsPage(this.trend.id, {
                search: this.tableEvent.filter.query,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortOrder: this.tableEvent.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([incidentStatus, bins, response]) => {
                this.totalElements = response.total;
                this.buildDataSource(response, incidentStatus, bins);
            });
    }

    navigateToIncident(incidentId: string, nestedTab: string): void {
        this.routerService.navigate(Path.INCIDENT_DETAILS, {
                pathParams: {
                    [INCIDENT_ID]: incidentId,
                },
                queryParams: {
                    [NESTED_TAB]: nestedTab,
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'trendIncidents',
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'createdDate',
                direction: 'desc',
            },
        };
    }

    private buildDataSource(
        response: TrendIncidentsResponse,
        incidentStatuses: ReferenceBookResponse[],
        bins: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.items.map((item, index) => {
            const related = item.relatedOrganizations?.map(r => findValueInDictionary(bins, r, 'name_ru'));
            const incidentStatus = findValueInDictionary(incidentStatuses, item.incidentStatus, 'name_ru');
            const governmentBin = findValueInDictionary(bins, item.governmentBin, 'name_ru');
            const createdByOrganization = findValueInDictionary(bins, item.incidentCreatedByOrganization, 'name_ru');
            const resolutionStatus = findValueInDictionary(incidentStatuses, item.resolutionStatus, 'name_ru');
            const row: Row = {
                incidentId: <Column>{
                    type: ColumnType.LINK,
                    text: item.incidentId,
                    click: () => this.navigateToIncident(item.incidentId, INCIDENT_DETAILS_TAB),
                },
                related: <Column>{
                    type: ColumnType.TEXT,
                    text: related?.length && related[0] || '-',
                    cellStyle: related?.join('\n')?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        ellipsisTextLarge: true,
                    },
                    tooltip: related?.join('\n')?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: related?.join('\n'),
                    },
                },
                governmentOrganization: <Column>{
                    type: ColumnType.TEXT,
                    text: governmentBin || '-',
                    cellStyle: governmentBin?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: governmentBin?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: governmentBin || '-',
                    },
                },
                createdByOrganization: <Column>{
                    type: ColumnType.TEXT,
                    text: createdByOrganization || '-',
                    cellStyle: createdByOrganization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: createdByOrganization?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH && {
                        text: createdByOrganization || '-',
                    },
                },
                incidentStatus: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: incidentStatus || '-',
                    style: mapIncidentStatusToStyle(incidentStatus),
                },
                ...(!item.resolutionStatus ? {resolutionStatus: <Column>{type: ColumnType.TEXT, text: ''}} : {
                    resolutionStatus: <ButtonColumn>{
                        type: ColumnType.BUTTON,
                        buttonType: 'text',
                        icon: 'ic_new_tab',
                        buttonSize: 'small',
                        text: resolutionStatus,
                        click: () => this.navigateToIncident(item.incidentId, INCIDENT_RESOLUTION_TAB),
                    }
                }),
                createdDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.incidentCreatedDate,
                },
            };
            return row;
        });
    }
}
